import { useCallback, useEffect, useState } from "react";
import {
    Box,
    Button,
    CircularProgress,
    FormControl,
    FormControlLabel,
    FormGroup,
    Input,
    InputLabel,
    MenuItem,
    Select,
    Slider,
    Switch,
    TextField,
    Typography,
} from "@mui/material";
import { Save } from "@mui/icons-material";

const ChangeAgentForm = () => {
    const backendEndpoint = process.env.REACT_APP_BACKEND_ENDPOINT;

    const [agentName, setAgentName] = useState("");
    const [agentGender, setAgentGender] = useState("");
    const [voiceTone, setVoiceTone] = useState("");
    const [answerLength, setAnswerLength] = useState("");
    const [waitMessageTime, setWaitMessageTime] = useState(0);

    const [typingEmulation, setTypingEmulation] = useState(false);
    const [wordsPerMinute, setWordsPerMinute] = useState(0);
    const [maxDelayInSeconds, setMaxDelayInSeconds] = useState(0);
    const [splitMessages, setSplitMessages] = useState(false);

    const [maxFollowUps, setMaxFollowUps] = useState(0);
    const [finalAction, setFinalAction] = useState("");
    const [hasFinalActionMessage, setHasFinalActionMessage] = useState(false);
    const [intervalAmount, setIntervalAmount] = useState(0);
    const [intervalType, setIntervalType] = useState("");

    const [isLoading, setIsLoading] = useState(false);

    const [validate, setValidate] = useState(false);

    const getCookieValue = useCallback(
        (name) =>
            document.cookie
                .match("(^|;)\\s*" + name + "\\s*=\\s*([^;]+)")
                ?.pop() || "",
        [],
    );

    const clearValidations = () => {
        setValidate(false);
    };

    const getAgentSettings = useCallback(async () => {
        setIsLoading(true);

        const response = await fetch(`${backendEndpoint}/agent/`, {
            method: "GET",
            credentials: "include",
            headers: {
                "Content-Type": "application/json",
                "X-CSRF-Token": getCookieValue("csrfToken"),
            },
        });

        const {
            name,
            gender,
            voiceTone,
            answerLength,
            waitMessageTime,
            typingEmulation,
            wordsPerMinute,
            maxDelayInSeconds,
            splitMessages,
            maxFollowUps,
            finalAction,
            hasFinalActionMessage,
            intervalBetweenActions: intervalInMinutes,
        } = await response.json();

        setAgentName(name);
        setAgentGender(gender);
        setVoiceTone(voiceTone);
        setAnswerLength(answerLength);
        setWaitMessageTime(waitMessageTime);

        setTypingEmulation(typingEmulation);
        setWordsPerMinute(wordsPerMinute);
        setMaxDelayInSeconds(maxDelayInSeconds);
        setSplitMessages(splitMessages);

        const minutesInOneDay = 1440;
        const minutesInOneHour = 60;
        setMaxFollowUps(maxFollowUps);
        setFinalAction(finalAction);
        setHasFinalActionMessage(hasFinalActionMessage);
        if (Math.trunc(intervalInMinutes / minutesInOneDay) > 0) {
            const days = intervalInMinutes / minutesInOneDay;
            setIntervalAmount(days);
            setIntervalType("days");
        } else if (Math.trunc(intervalInMinutes / minutesInOneHour) > 0) {
            const hours = intervalInMinutes / minutesInOneHour;
            setIntervalAmount(hours);
            setIntervalType("hours");
        } else {
            setIntervalAmount(intervalInMinutes);
            setIntervalType("minutes");
        }

        setIsLoading(false);
    }, [backendEndpoint, getCookieValue]);

    const handleUpdateAgentSettings = () => {
        setValidate(true);
        if (!agentName || !agentGender || !voiceTone || !answerLength) return;

        setIsLoading(true);

        const intervalMultiplier =
            intervalType === "days" ? 1440 : intervalType === "hours" ? 60 : 1;
        fetch(`${backendEndpoint}/agent/`, {
            method: "POST",
            credentials: "include",
            headers: {
                "Content-Type": "application/json",
                "X-CSRF-Token": getCookieValue("csrfToken"),
            },
            body: JSON.stringify({
                name: agentName,
                gender: agentGender,
                voiceTone,
                answerLength,
                waitMessageTime,
                typingEmulation,
                wordsPerMinute,
                maxDelayInSeconds,
                splitMessages,
                maxFollowUps,
                finalAction,
                hasFinalActionMessage,
                intervalBetweenActions: Math.trunc(
                    intervalAmount * intervalMultiplier,
                ),
            }),
        })
            .then(async (response) => {
                if (response.ok) {
                    const result = await response.json();
                    return result;
                } else {
                    const result = await response.json();
                    throw new Error(result.detail);
                }
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    useEffect(() => {
        getAgentSettings();
    }, [getAgentSettings]);

    return (
        <>
            <TextField
                value={agentName}
                onChange={(e) => {
                    setAgentName(e.target.value);
                    clearValidations();
                }}
                label="Nome do Agente*"
                variant="outlined"
                fullWidth
                margin="normal"
                error={validate && agentName === "" ? true : false}
                helperText={
                    validate && agentName === ""
                        ? "Preencha o nome do seu agente"
                        : ""
                }
            />

            <FormControl
                fullWidth
                sx={{
                    marginTop: "16px",
                    marginBottom: "8px",
                }}
            >
                <InputLabel id="agente-gender-select-label">
                    Tipo de voz*
                </InputLabel>
                <Select
                    labelId="agente-gender-select-label"
                    value={agentGender}
                    label="Tipo de voz*"
                    onChange={(e) => setAgentGender(e.target.value)}
                >
                    <MenuItem value="male">Masculina</MenuItem>
                    <MenuItem value="female">Feminina</MenuItem>
                </Select>
            </FormControl>

            <FormControl
                fullWidth
                sx={{
                    marginTop: "16px",
                    marginBottom: "8px",
                }}
            >
                <InputLabel id="agente-voice-tone-select-label">
                    Tom de voz*
                </InputLabel>
                <Select
                    labelId="agente-voice-tone-select-label"
                    value={voiceTone}
                    label="Tom de voz*"
                    onChange={(e) => setVoiceTone(e.target.value)}
                >
                    <MenuItem value="standard">Padrão</MenuItem>
                    <MenuItem value="matter-of-fact">Direto</MenuItem>
                    <MenuItem value="professional">Profissional</MenuItem>
                    <MenuItem value="friendly">Amigável</MenuItem>
                    <MenuItem value="humorous">Bem humorado</MenuItem>
                    <MenuItem value="emotional">Emocional</MenuItem>
                </Select>
            </FormControl>

            <FormControl
                fullWidth
                sx={{
                    marginTop: "16px",
                    marginBottom: "8px",
                }}
            >
                <InputLabel id="agente-answer-length-select-label">
                    Comprimento da resposta*
                </InputLabel>
                <Select
                    labelId="agente-answer-length-select-label"
                    value={answerLength}
                    label="Comprimento da resposta*"
                    onChange={(e) => setAnswerLength(e.target.value)}
                >
                    <MenuItem value="succinct">Sucinto</MenuItem>
                    <MenuItem value="standard">Padrão</MenuItem>
                    <MenuItem value="comprehensive">Compreensivo</MenuItem>
                </Select>
            </FormControl>

            <FormControl
                fullWidth
                sx={{
                    border: "1px solid #cdcdcd",
                    borderRadius: 1,
                    height: "56px",
                    paddingLeft: 2,
                    paddingRight: 2,
                    marginTop: "16px",
                    marginBottom: "8px",
                    display: "flex",
                    justifyContent: "center",
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                    }}
                >
                    <Typography sx={{ mr: 2 }}>
                        Aguardar mensagem (em segundos):
                    </Typography>
                    <Slider
                        value={waitMessageTime}
                        onChange={(e) => setWaitMessageTime(e.target.value)}
                        min={3}
                        max={60}
                        step={1}
                        sx={{ maxWidth: "400px" }}
                    />
                    <Input
                        value={waitMessageTime}
                        type="number"
                        size="small"
                        onChange={(e) => setWaitMessageTime(e.target.value)}
                        onBlur={(e) =>
                            setWaitMessageTime(
                                parseInt(
                                    Math.max(0, Math.min(60, e.target.value)),
                                ),
                            )
                        }
                        sx={{ ml: 2, maxWidth: "50px" }}
                    />
                </Box>
            </FormControl>

            <FormControl
                fullWidth
                sx={{
                    border: "1px solid #cdcdcd",
                    borderRadius: 1,
                    paddingLeft: 2,
                    paddingRight: 2,
                    marginTop: "16px",
                    marginBottom: "8px",
                    minHeight: "56px",
                    display: "flex",
                    justifyContent: "center",
                    paddingTop: "9px",
                    paddingBottom: "9px",
                }}
            >
                <FormGroup>
                    <FormControlLabel
                        label="Emulação de digitação"
                        control={
                            <Switch
                                checked={typingEmulation}
                                onChange={(e) =>
                                    setTypingEmulation(e.target.checked)
                                }
                            />
                        }
                    />

                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                        }}
                    >
                        <Typography sx={{ mr: 2 }}>
                            Palavras por minuto:
                        </Typography>
                        <Slider
                            value={wordsPerMinute}
                            onChange={(e) => setWordsPerMinute(e.target.value)}
                            min={80}
                            max={800}
                            step={10}
                            sx={{ maxWidth: "400px" }}
                        />
                        <Input
                            value={wordsPerMinute}
                            type="number"
                            size="small"
                            onChange={(e) => setWordsPerMinute(e.target.value)}
                            onBlur={(e) =>
                                setWordsPerMinute(
                                    parseInt(
                                        Math.max(
                                            80,
                                            Math.min(800, e.target.value),
                                        ),
                                    ),
                                )
                            }
                            sx={{ ml: 2, maxWidth: "50px" }}
                        />
                    </Box>

                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                        }}
                    >
                        <Typography sx={{ mr: 2 }}>
                            Atraso máximo (em segundos):
                        </Typography>
                        <Slider
                            value={maxDelayInSeconds}
                            onChange={(e) =>
                                setMaxDelayInSeconds(e.target.value)
                            }
                            min={0}
                            max={10}
                            step={1}
                            sx={{ maxWidth: "400px" }}
                        />
                        <Input
                            value={maxDelayInSeconds}
                            type="number"
                            size="small"
                            onChange={(e) =>
                                setMaxDelayInSeconds(e.target.value)
                            }
                            onBlur={(e) =>
                                setMaxDelayInSeconds(
                                    parseInt(
                                        Math.max(
                                            0,
                                            Math.min(10, e.target.value),
                                        ),
                                    ),
                                )
                            }
                            sx={{ ml: 2, maxWidth: "50px" }}
                        />
                    </Box>

                    <FormControlLabel
                        label="Dividir mensagens"
                        control={
                            <Switch
                                checked={splitMessages}
                                onChange={(e) =>
                                    setSplitMessages(e.target.checked)
                                }
                            />
                        }
                    />
                </FormGroup>
            </FormControl>

            <FormControl
                fullWidth
                sx={{
                    border: "1px solid #cdcdcd",
                    borderRadius: 1,
                    paddingLeft: 2,
                    paddingRight: 2,
                    marginTop: "16px",
                    marginBottom: "8px",
                    minHeight: "56px",
                    display: "flex",
                    justifyContent: "center",
                    paddingTop: "9px",
                    paddingBottom: "9px",
                }}
            >
                <FormGroup>
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            width: "100%",
                            mt: 1,
                        }}
                    >
                        <Typography sx={{ mr: 2 }}>
                            Ações de follow-up:
                        </Typography>
                        <Slider
                            value={maxFollowUps}
                            onChange={(e) => setMaxFollowUps(e.target.value)}
                            min={0}
                            max={5}
                            step={1}
                            sx={{ maxWidth: "400px" }}
                        />
                        <Input
                            value={maxFollowUps}
                            type="number"
                            size="small"
                            onChange={(e) => setMaxFollowUps(e.target.value)}
                            onBlur={(e) =>
                                setMaxFollowUps(
                                    parseInt(
                                        Math.max(
                                            0,
                                            Math.min(5, e.target.value),
                                        ),
                                    ),
                                )
                            }
                            sx={{ ml: 2, maxWidth: "50px" }}
                        />
                    </Box>
                </FormGroup>

                <FormControl
                    sx={{
                        marginTop: "16px",
                        marginBottom: "8px",
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                        }}
                    >
                        <Typography sx={{ mr: 2 }}>Ação final:</Typography>
                        <Select
                            value={finalAction}
                            onChange={(e) => setFinalAction(e.target.value)}
                            sx={{ maxWidth: "400px" }}
                        >
                            <MenuItem value="close">Encerrar conversa</MenuItem>
                            <MenuItem value="handoff">
                                Transferir conversa
                            </MenuItem>
                        </Select>
                    </Box>
                </FormControl>

                <FormGroup
                    sx={{
                        marginTop: "16px",
                        marginBottom: "8px",
                    }}
                >
                    <FormControlLabel
                        label="Envio de mensagem na ação final"
                        control={
                            <Switch
                                checked={hasFinalActionMessage}
                                onChange={(e) =>
                                    setHasFinalActionMessage(e.target.checked)
                                }
                            />
                        }
                    />
                </FormGroup>

                <FormControl
                    sx={{
                        marginTop: "16px",
                        marginBottom: "8px",
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                        }}
                    >
                        <Typography sx={{ mr: 2 }}>
                            Intervalo entre as ações:
                        </Typography>

                        <Input
                            value={intervalAmount}
                            type="number"
                            size="small"
                            onChange={(e) => setIntervalAmount(e.target.value)}
                            onBlur={(e) =>
                                setIntervalAmount(
                                    parseInt(
                                        Math.max(
                                            intervalType === "minutes" ? 5 : 1,
                                            Math.min(
                                                intervalType === "minutes"
                                                    ? 60
                                                    : intervalType === "hours"
                                                      ? 24
                                                      : 7,
                                                e.target.value,
                                            ),
                                        ),
                                    ),
                                )
                            }
                            sx={{ mr: 2, maxWidth: "50px" }}
                        />

                        <Select
                            value={intervalType}
                            onChange={(e) => {
                                setIntervalType(e.target.value);
                                setIntervalAmount(
                                    e.target.value === "minutes" ? 5 : 1,
                                );
                            }}
                            sx={{ maxWidth: "400px" }}
                        >
                            <MenuItem value="minutes">minuto(s)</MenuItem>
                            <MenuItem value="hours">hora(s)</MenuItem>
                            <MenuItem value="days">dia(s)</MenuItem>
                        </Select>
                    </Box>
                </FormControl>
            </FormControl>

            <Button
                variant="contained"
                color="primary"
                sx={{
                    marginBottom: 2,
                    marginTop: 2,
                    borderRadius: "25px",
                    padding: "8px 40px 6px",
                    textTransform: "none",
                    float: "right",
                }}
                disabled={isLoading}
                startIcon={
                    isLoading ? (
                        <CircularProgress size="16px" color="inherit" />
                    ) : (
                        <Save />
                    )
                }
                onClick={handleUpdateAgentSettings}
            >
                Salvar
            </Button>
        </>
    );
};

export default ChangeAgentForm;
